import type { UpsertRecentUsersMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

/**
 * Maps the tRPC mutation response to match the GraphQL mutation response shape
 * so consumers branching on the result do not need to know which path executed.
 */
const mapTrpcResponseToGraphQL = (data: {
  affected_rows: number;
}): UpsertRecentUsersMutation => ({
  insert_user_search_preferences: {
    __typename: 'user_search_preferences_mutation_response',
    affected_rows: data.affected_rows,
  },
});

export const useUpsertRecentUsersTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.userSearchPreferences.upsertRecentUsers.mutationOptions({
      onSuccess: async () => {
        // Mirror the legacy Apollo `evictField(cache, 'user_search_preferences')`
        // so the read re-fetches the updated row.
        await queryClient.invalidateQueries({
          queryKey:
            trpc.frontend.userSearchPreferences.getUserSearchPreferences.queryKey(),
        });
      },
    }),
  });

  return {
    upsertRecentUsers: async (
      RecentUserIds: string[]
    ): Promise<UpsertRecentUsersMutation> => {
      const result = await mutation.mutateAsync({ RecentUserIds });

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
